import router from './appRoutes';
import { useSessionStore } from '@/stores/session';
import { verifyPermission } from '@/composables/verifyPermission';
import { useUserStore } from '@/stores/user';
import { useBannerStore } from '@/stores/banners';

const nextWithBanner = (next: CallableFunction, verifyBanner: CallableFunction, to: string) => {
  next();
  verifyBanner(to);
};

const hasExternalAuthParams = (to?: { query?: Record<string, any> }) => {
  if (to?.query?.auth_from === 'ecx-auth') {
    return true;
  }
  const params = new URLSearchParams(window.location.search);
  return params.get('auth_from') === 'ecx-auth';
};

const isAuthRoute = (to: { name?: string | symbol | null; path?: string }) => {
  const name = to.name;
  const path = to.path || '';
  
  const publicAuthPaths = ['/login', '/auth-callback', '/redefinir-senha', '/esqueci-minha-senha', '/validar-token'];
  const publicAuthNames = ['auth-callback', 'redefinir-senha', 'esqueci-minha-senha'];
  
  if (publicAuthPaths.some(p => path === p || path.startsWith(p + '?') || path.startsWith(p + '/'))) {
    return true;
  }
  
  if (!name || typeof name !== 'string') return false;
  return publicAuthNames.includes(name) || name.startsWith('autenticacao-2-fatores');
};

router.beforeEach(async (to, _from, next) => {
  const sessionStore = useSessionStore();
  const userStore = useUserStore();

  const skipRefresh = isAuthRoute(to) || hasExternalAuthParams(to);

  if (!skipRefresh && sessionStore.hasSession) {
    const needsRefresh = !userStore.currentUser?.name || !userStore.currentUser?.email;
    if (needsRefresh) await userStore.refreshUser({ refresh_token: true, silent: true });
  }

  if (to.meta.requiresAuth && !sessionStore.hasSession) {
    try {
      const session = await userStore.checkSession();
      if (session?.authenticated) {
        await userStore.refreshUser({ silent: true });
        return next();
      }
    } catch {}
    
    next({ path: '/login' });
  } else {
    next();
  }
});

router.beforeResolve(async (to, _, next) => {
  const store = useSessionStore();
  const userStore = useUserStore();
  const { verifyBannerRoute } = useBannerStore();

  if (isAuthRoute(to) || hasExternalAuthParams(to)) {
    return next();
  }

  if (to.meta.requiresBeingRoot) {
    return userStore.isRoot ? nextWithBanner(next, verifyBannerRoute, to.fullPath) : next({ name: 'tela-inicial' });
  }

  if (store.hasSession && !userStore.isRoot && userStore.currentUser && !userStore.getPermissions?.length) {
    await userStore.fetchPermissions();
  }

  if (to.meta.requiresPermission) {
    const hasPermission = to.meta.requiresPermission.some((permission) => {
      return verifyPermission(permission.resource, permission.action);
    });

    return hasPermission ? nextWithBanner(next, verifyBannerRoute, to.fullPath) : next({ name: 'tela-inicial' });
  }

  return nextWithBanner(next, verifyBannerRoute, to.fullPath);
});

export default router;
